// Enum for all possible search fields
export enum SearchField {
    TRANSIT_START_TIME_UTC = 'transit.start_time_utc',
    TRANSIT_END_TIME_UTC = 'transit.end_time_utc',
    COMMENTS = 'comments',
    METOCEAN_REGION = 'metocean.region',
    METOCEAN_SOURCE_ID = 'metocean.source_id',
    METOCEAN_SLUG = 'metocean.source_slug',
    METOCEAN_DIRECTION_FROM_DEG = 'metocean.direction_from_deg',
    METOCEAN_DIRECTION_TO_DEG = 'metocean.direction_to_deg',
    METOCEAN_GUST_KN = 'metocean.gust_kn',
    METOCEAN_HEIGHT_M = 'metocean.height_m',
    METOCEAN_SPEED_KN = 'metocean.speed_kn',
    METOCEAN_HS_SEA_M = 'metocean.hs_sea_m',
    METOCEAN_HS_SWELL_M = 'metocean.hs_swell_m',
    METOCEAN_HS_TOTAL_M = 'metocean.hs_total_m',
    METOCEAN_TP_SEA_M = 'metocean.tp_sea_m',
    METOCEAN_TP_SWELL_M = 'metocean.tp_swell_m',
    METOCEAN_TP_TOTAL_M = 'metocean.tp_total_m',
    METOCEAN_DIRECTION_DEG = 'metocean.direction_deg',
    METOCEAN_TM_SEA_S = 'metocean.tm_sea_s',
    METOCEAN_TM_SWELL_S = 'metocean.tm_swell_s',
    METOCEAN_TM_TOTAL_S = 'metocean.tm_total_s',
    METOCEAN_TP_SEA_S = 'metocean.tp_sea_s',
    METOCEAN_TP_SWELL_S = 'metocean.tp_swell_s',
    METOCEAN_TP_TOTAL_S = 'metocean.tp_total_s',
    TAGS_ANALYSIS = 'tags.analysis',
    TAGS_POSITIONING = 'tags.positioning',
    TAGS_SYSTEM = 'tags.system',
    TAGS_TYPE = 'tags.type',
    TAGS_USER = 'tags.user',
    VESSEL_BEAM_M = 'vessel.beam_m',
    VESSEL_DRAFT_M = 'vessel.draft_m',
    VESSEL_LOA_M = 'vessel.loa_m',
    VESSEL_NAME = 'vessel.shipname',
    VESSEL_TYPE = 'vessel.vessel_type',
}

// Enum for all possible condition types
enum ConditionType {
    AND = 'and', // Logical AND condition combining multiple conditions
    OR = 'or', // Logical OR condition combining multiple conditions
    NOT = 'not', // Logical NOT condition negating a set of conditions
    EXISTS = 'exists', // Represents an exists query, used to search for documents where a field is present or contains a value.
    RANGE = 'range', // Represents a range query, used to search for documents with field values within a specified range.
    TERM = 'term', // Represents a term query, used to search for documents where a field matches an exact value.
    TERMS = 'terms', // Represents a terms query, used to search for documents where a field matches any value from a list of terms.
    WILDCARD = 'wildcard', // Represents an wildcard query, used to search for documents using wildcard, where * represents any character.
}

// Base interface for query conditions (those with fields)
interface QueryCondition {
    type:
        | ConditionType.EXISTS
        | ConditionType.RANGE
        | ConditionType.TERM
        | ConditionType.TERMS
        | ConditionType.WILDCARD;
    field: string; // The field being queried
}

// Exists condition type, used to search for documents where a field is present and not empty.
interface ExistsCondition extends QueryCondition {
    type: ConditionType.EXISTS;
}

// Range Condition Values
type RangeValue = {
    gte?: string | number; // Greater than or equal to a value
    lte?: string | number; // Less than or equal to a value
    gt?: string | number; // Greater than a value
    lt?: string | number; // Less than a value
};

// Range condition type, used to search for documents with field values within a specified range
interface RangeCondition extends QueryCondition {
    type: ConditionType.RANGE;
    value: RangeValue;
}

// Term condition type, used to search for documents where a field matches an exact value.
interface TermCondition extends QueryCondition {
    type: ConditionType.TERM;
    value: string | number; // The exact value to match
}

// Terms condition type, used to search for documents where a field matches any value from a list of terms.
interface TermsCondition extends QueryCondition {
    type: ConditionType.TERMS;
    value: (string | number)[]; // Array of values to match
}

// Wildcard condition type, used to search for documents using wildcare, where * represents any character.
interface WildcardCondition extends QueryCondition {
    type: ConditionType.WILDCARD;
    value: string; // For example, *mae* or *mae*kat*
}

// Base interface for logical conditions (AND, OR, NOT)
interface LogicalCondition {
    // eslint-disable-next-line no-use-before-define
    conditions: Condition[]; // Array of conditions for composite queries
}

// Logical AND condition
interface AndCondition extends LogicalCondition {
    type: ConditionType.AND;
}

// Logical OR condition
interface OrCondition extends LogicalCondition {
    type: ConditionType.OR;
}

// Logical NOT condition
interface NotCondition extends LogicalCondition {
    type: ConditionType.NOT;
}
// Union type for all possible conditions
type Condition =
    | ExistsCondition
    | RangeCondition
    | TermCondition
    | TermsCondition
    | WildcardCondition
    | AndCondition
    | OrCondition
    | NotCondition;

export class QueryBuilder {
    exists(field: string): ExistsCondition {
        const condition: ExistsCondition = {
            type: ConditionType.EXISTS,
            field,
        };
        return condition;
    }
    range(field: string, value: RangeValue): RangeCondition {
        const condition: RangeCondition = {
            type: ConditionType.RANGE,
            field,
            value,
        };
        return condition;
    }
    term(field: string, value: string | number): TermCondition {
        const condition: TermCondition = {
            type: ConditionType.TERM,
            field,
            value,
        };
        return condition;
    }
    terms(field: string, value: (string | number)[]): TermsCondition {
        const condition: TermsCondition = {
            type: ConditionType.TERMS,
            field,
            value,
        };
        return condition;
    }
    wildcard(field: string, value: string): WildcardCondition {
        const condition: WildcardCondition = {
            type: ConditionType.WILDCARD,
            field,
            value,
        };
        return condition;
    }
    and_condition(conditions: Condition[]): AndCondition {
        const condition: AndCondition = {
            type: ConditionType.AND,
            conditions,
        };
        return condition;
    }
    or_condition(conditions: Condition[]): OrCondition {
        const condition: OrCondition = {
            type: ConditionType.OR,
            conditions,
        };
        return condition;
    }
    not_condition(conditions: Condition[]): NotCondition {
        const condition: NotCondition = {
            type: ConditionType.NOT,
            conditions,
        };
        return condition;
    }
}

// // Examples
// const builder = new QueryBuilder();

// // Full search query
// const full_search_query = builder.and_condition([
//     builder.range(SearchField.TRANSIT_START_TIME_UTC, { gte: '2024-01-01T01:00:00Z' }),
//     builder.range(SearchField.TRANSIT_END_TIME_UTC, { lte: '2024-12-01T01:00:00Z' }),
//     builder.wildcard(SearchField.VESSEL_NAME, '*MAE*'),
//     builder.terms(SearchField.VESSEL_TYPE, ['Bulk Carrier', 'Container', 'Tanker']),
//     builder.range(SearchField.VESSEL_LOA_M, { gte: 20, lte: 200 }),
//     builder.range(SearchField.VESSEL_BEAM_M, { gte: 20, lte: 200 }),
//     builder.terms(SearchField.TAGS_SYSTEM, ['Data gap', 'For Review']), // Replace with Tag UUIDs
//     builder.terms(SearchField.TAGS_TYPE, ['outbound', 'inbound']), // Replace with Tag UUIDs
//     builder.terms(SearchField.TAGS_POSITIONING, ['Pilot Boarding Ground', 'Berth 1']), // Replace with Tag UUIDs
//     builder.terms(SearchField.TAGS_ANALYSIS, ['Grade A', 'Grade B']), // Replace with Tag UUIDs
//     builder.terms(SearchField.TAGS_USER, ['User public tag 1', 'User private tag 2']), // Replace with Tag UUIDs
//     builder.exists(SearchField.COMMENTS),
//     builder.or_condition([
//         builder.and_condition([
//             builder.term(SearchField.METOCEAN_REGION, 'port_area'),
//             builder.term(SearchField.METOCEAN_SOURCE_ID, '378070c3-aacb-42cb-9ba4-733ae429266e'), // Wind - A Beacon
//             builder.range(SearchField.METOCEAN_GUST_KN, { gte: 5, lte: 40 }),
//             builder.range(SearchField.METOCEAN_SPEED_KN, { gte: 5, lte: 40 }),
//             builder.or_condition([
//                 builder.range(SearchField.METOCEAN_DIRECTION_FROM_DEG, { gte: 0, lte: 45 }),
//                 builder.range(SearchField.METOCEAN_DIRECTION_FROM_DEG, { gte: 315, lte: 360 }),
//             ]),
//         ]),
//         builder.and_condition([
//             builder.term(SearchField.METOCEAN_REGION, 'port_area'),
//             builder.term(SearchField.METOCEAN_SOURCE_ID, '37548345-e4e3-44fe-b50f-b479e2af7cf7'), // Wind - Crane 7
//             builder.range(SearchField.METOCEAN_GUST_KN, { gte: 5, lte: 40 }),
//             builder.range(SearchField.METOCEAN_SPEED_KN, { gte: 5, lte: 40 }),
//             builder.or_condition([
//                 builder.range(SearchField.METOCEAN_DIRECTION_FROM_DEG, { gte: 0, lte: 45 }),
//                 builder.range(SearchField.METOCEAN_DIRECTION_FROM_DEG, { gte: 315, lte: 360 }),
//             ]),
//         ]),
//     ]),
//     builder.or_condition([
//         builder.and_condition([
//             builder.term(SearchField.METOCEAN_REGION, 'port_area'),
//             builder.term(SearchField.METOCEAN_SOURCE_ID, '5bd2f206-5297-4c26-a773-7bf22d668131'), // Tide - A Beacon
//             builder.range(SearchField.METOCEAN_HEIGHT_M, { gte: 1, lte: 2 }),
//         ]),
//         builder.and_condition([
//             builder.term(SearchField.METOCEAN_REGION, 'port_area'),
//             builder.term(SearchField.METOCEAN_SOURCE_ID, '5fe34aca-c891-40c2-8799-93fd3a54acbc'), // Tide - Tug Berth
//             builder.range(SearchField.METOCEAN_HEIGHT_M, { gte: 1, lte: 3 }),
//         ]),
//     ]),
// ]);

// // Recent transit query
// const last_hour = new Date(new Date().getTime() - 60 * 60 * 1000);
// const last_hour_utc = last_hour.toISOString();
// const recent_transits_query = builder.and_condition([
//     builder.range(SearchField.TRANSIT_START_TIME_UTC, { gte: last_hour_utc }),
// ]);

// // Transits that overlap start and end time query
// const start_time = '2024-12-01T01:00:00Z';
// const end_time = '2024-12-01T02:00:00Z';
// const overlap_transits_query = builder.and_condition([
//     builder.range(SearchField.TRANSIT_START_TIME_UTC, { lt: end_time }),
//     builder.range(SearchField.TRANSIT_END_TIME_UTC, { gt: start_time }),
// ]);
